import { Input, Select } from '@innowise-group/mui-kit';
import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Styled from './working-section.styles';
import { SelectOption, useOptionsApi, useProfileAPI } from '@innowise-group/core';
import { FileTypeConstants, RolesValues } from '@constants';
import { Dropzone } from '../dropzone';
import { EmployeeField } from '@shared-mui-components';

interface WorkingSectionProps {
  isDiff?: boolean;
  disabled?: boolean;
}

const WorkingSection: React.FC<WorkingSectionProps> = ({ isDiff, disabled }) => {
  const { t } = useTranslation();

  const { control, watch, setValue } = useFormContext();
  const { profile } = useProfileAPI();

  const { professionOptions, gradeOptions, unitOptions, divisionOptions, departmentOptions } = useOptionsApi();

  const isCandidateAlreadyLinked = !!watch('hrmId');

  const hasAccessToManageField = useMemo(() => {
    return [RolesValues.Admin, RolesValues.Recruiter, RolesValues.Sourcer].includes(profile?.role?.name);
  }, [profile.role?.name]);

  const handleChangeField =
    (onChange: (value: string) => void, fieldName: string, options: SelectOption[]) => (value: string) => {
      onChange(value);
      setValue(fieldName, options.find((item) => item.value === value)?.title || '');
    };

  return (
    <>
      <Styled.GroupFieldsContainer>
        <Styled.BoxForDiffConsolidation>
          {isDiff && <Styled.CustomPrimitiveFieldsDiff fieldName="hrmEmail" />}

          <Controller
            name="hrmEmail"
            control={control}
            render={({ field: { onChange, value }, formState: { errors } }) => {
              return (
                <Input
                  value={value}
                  onChange={onChange}
                  label={t('pages.candidates.candidateCreation.corporateEmail')}
                  placeholder={t('pages.candidates.candidateCreation.enterTheEmail')}
                  disabled={disabled || isCandidateAlreadyLinked}
                  error={!!errors['hrmEmail']?.message}
                  helperText={errors['hrmEmail']?.message.toString()}
                />
              );
            }}
          />
        </Styled.BoxForDiffConsolidation>

        <Styled.BoxForDiffConsolidation>
          {isDiff && <Styled.CustomIsOutsideHrmDiff />}

          <Controller
            name="isOutsideHrm"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Styled.CustomCheckbox
                onChange={onChange}
                checked={value}
                label={t('pages.candidates.candidateCreation.notCreatedInHRM')}
                disabled={disabled || isCandidateAlreadyLinked}
              />
            )}
          />
        </Styled.BoxForDiffConsolidation>
      </Styled.GroupFieldsContainer>

      <Styled.GroupFieldsContainer>
        <Styled.BoxForDiffConsolidation>
          {isDiff && <Styled.CustomOrgUnitCandidatePreofferInfoDiff fieldName="candidatePreofferInfo__unit" />}

          <Controller
            name="candidatePreofferInfo.unitId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                disabled={disabled || !hasAccessToManageField}
                value={value}
                onChange={handleChangeField(onChange, 'candidatePreofferInfo.unitName', unitOptions)}
                options={unitOptions}
                renderInput={(params) => {
                  return (
                    <Input
                      {...params}
                      label={t('pages.candidates.candidateCreation.unit')}
                      placeholder={t('common.placeholders.selectValue')}
                    />
                  );
                }}
              />
            )}
          />
        </Styled.BoxForDiffConsolidation>

        <Styled.BoxForDiffConsolidation>
          {isDiff && <Styled.CustomOrgUnitCandidatePreofferInfoDiff fieldName="candidatePreofferInfo__division" />}

          <Controller
            name="candidatePreofferInfo.divisionId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                disabled={disabled || !hasAccessToManageField}
                value={value}
                onChange={handleChangeField(onChange, 'candidatePreofferInfo.divisionName', divisionOptions)}
                options={divisionOptions}
                renderInput={(params) => {
                  return (
                    <Input
                      {...params}
                      label={t('pages.candidates.candidateCreation.division')}
                      placeholder={t('common.placeholders.selectValue')}
                    />
                  );
                }}
              />
            )}
          />
        </Styled.BoxForDiffConsolidation>

        <Styled.BoxForDiffConsolidation>
          {isDiff && <Styled.CustomOrgUnitCandidatePreofferInfoDiff fieldName="candidatePreofferInfo__department" />}

          <Controller
            name="candidatePreofferInfo.departmentId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                disabled={disabled || !hasAccessToManageField}
                value={value}
                onChange={handleChangeField(onChange, 'candidatePreofferInfo.departmentName', departmentOptions)}
                options={departmentOptions}
                renderInput={(params) => {
                  return (
                    <Input
                      {...params}
                      label={t('pages.candidates.candidateCreation.department')}
                      placeholder={t('common.placeholders.selectValue')}
                    />
                  );
                }}
              />
            )}
          />
        </Styled.BoxForDiffConsolidation>
      </Styled.GroupFieldsContainer>

      <Styled.GroupFieldsContainer>
        <Styled.BoxForDiffConsolidation>
          {isDiff && <Styled.CustomEmployeeDtoCandidatePreofferInfoDiff fieldName="candidatePreofferInfo__head" />}

          <EmployeeField
            parentField="candidatePreofferInfo"
            field="head"
            label="pages.candidates.candidateCreation.head"
            placeholder="common.placeholders.selectHead"
            disabled={disabled || !hasAccessToManageField}
          />
        </Styled.BoxForDiffConsolidation>

        <Styled.BoxForDiffConsolidation>
          {isDiff && (
            <Styled.CustomEmployeeDtoCandidatePreofferInfoDiff fieldName="candidatePreofferInfo__resourceManager" />
          )}

          <EmployeeField
            parentField="candidatePreofferInfo"
            field="resourceManager"
            label="pages.candidates.candidateCreation.resourceManager"
            placeholder="common.placeholders.selectManager"
            disabled={disabled || !hasAccessToManageField}
          />
        </Styled.BoxForDiffConsolidation>

        <Styled.BoxForDiffConsolidation>
          {isDiff && <Styled.CustomDictionaryCandidatePreofferInfoDiff fieldName="candidatePreofferInfo__position" />}

          <Controller
            name="candidatePreofferInfo.positionId"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <Select
                  disabled={disabled || !hasAccessToManageField}
                  value={value}
                  onChange={handleChangeField(onChange, 'candidatePreofferInfo.positionName', professionOptions)}
                  options={professionOptions}
                  disableSortByAlphabet={false}
                  renderInput={(params) => {
                    return (
                      <Input
                        {...params}
                        label={t('pages.candidates.candidateCreation.position')}
                        placeholder={t('common.placeholders.selectValue')}
                      />
                    );
                  }}
                />
              );
            }}
          />
        </Styled.BoxForDiffConsolidation>

        <Styled.BoxForDiffConsolidation>
          {isDiff && <Styled.CustomEnumCandidatePreofferInfoDiff fieldName="candidatePreofferInfo__grade" />}

          <Controller
            name="candidatePreofferInfo.grade"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <Select
                  disabled={disabled || !hasAccessToManageField}
                  value={value}
                  onChange={onChange}
                  options={gradeOptions}
                  renderInput={(params) => {
                    return (
                      <Input
                        {...params}
                        label={t('pages.candidates.candidateCreation.grade')}
                        placeholder={t('common.placeholders.selectValue')}
                      />
                    );
                  }}
                />
              );
            }}
          />
        </Styled.BoxForDiffConsolidation>
      </Styled.GroupFieldsContainer>

      <Styled.CommentWrapper>
        {isDiff && <Styled.CustomPrimitiveFieldsDiff fieldName="candidatePreofferInfo__comment" />}

        <Controller
          name="candidatePreofferInfo.comment"
          control={control}
          render={({ field: { onChange, value }, formState: { errors } }) => (
            <Input
              disabled={disabled || !hasAccessToManageField}
              label={`${t('pages.candidates.candidateCreation.comment')}`}
              error={!!errors.candidatePreofferInfo?.['comment']?.message}
              helperText={errors.candidatePreofferInfo?.['comment']?.message.toString()}
              value={value}
              multiline
              minRows={2}
              maxRows={12}
              onChange={onChange}
              placeholder={t('common.placeholders.fillComment')}
            />
          )}
        />
      </Styled.CommentWrapper>

      <Styled.GroupFieldsContainer>
        <Styled.BoxForDiffConsolidation>
          {isDiff && <Styled.CustomBooleanCandidatePreofferInfoDiff fieldName="candidatePreofferInfo__isCvAttached" />}

          <Controller
            name="candidatePreofferInfo.isCvAttached"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Styled.CustomCheckbox
                onChange={onChange}
                checked={value}
                label={t('pages.candidates.candidateCreation.attachedCV')}
                disabled={disabled || !hasAccessToManageField}
              />
            )}
          />
        </Styled.BoxForDiffConsolidation>

        <Styled.BoxForDiffConsolidation>
          {isDiff && <Styled.CustomBooleanCandidatePreofferInfoDiff fieldName="candidatePreofferInfo__isNdaAttached" />}

          <Controller
            name="candidatePreofferInfo.isNdaAttached"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Styled.CustomCheckbox
                onChange={onChange}
                checked={value}
                label={t('pages.candidates.candidateCreation.attachedNDA')}
                disabled={disabled || !hasAccessToManageField}
              />
            )}
          />
        </Styled.BoxForDiffConsolidation>
      </Styled.GroupFieldsContainer>

      <Dropzone
        fieldName="candidatePreofferInfo.attachments"
        fileType={FileTypeConstants.CandidatePreoffer}
        disabled={disabled}
      />
    </>
  );
};

export default React.memo(WorkingSection);
